import NavComponent from "../lib/nav_component";
import {
    PaneLinkComponent,
    PaneLinkComponentConfig,
} from "./pane_link_component";
import { PaneInfoComponent } from "./pane_info_component";
import { OnlineAppNavOptionConfig } from "../../components/online_app_nav_option";
import { primaryPaneOptionCollection } from "../instances/primary_pane_option_collection";

export const PaneOptionComponentConfig = {
    componentTypeKey: "PaneOptionComponent",
    baseHtmlClass: "nav_option",
    selector: ".nav_option",

    datasetConfigKey: "nav_option__online_app_nav_data",
    identifierDatasetConfigKey: "identifier",
    statsIdentifierDatasetConfigKey: "stats_identifier",
    paneIdentifierDatasetConfigKey: "nav_pane_identifier",
    nativeViewStateDatasetConfigKey: "has_native_view",
    preventDefaultActionDatasetConfigKey: "prevent_default_action",
    alwaysOpenPaneDatasetConfigKey: "always_open_pane",
    navToggleInstructionDatasetConfigKey: "nav_toggle_instruction",

    // The following are deprecated in favour of getting config via the dataset config JSON (above).
    identifierDatasetKey: "nav_option__identifier",
    paneIdentifierDatasetKey: "nav_pane__identifier",
    hasNativeViewDatasetKey: "nav_option__has_native_view",
    preventDefaultActionDatasetKey: "nav_option__prevent_default_action",
};

export class PaneOptionComponent extends NavComponent {
    constructor(id_or_element, primaryPane, panesManager) {
        super(id_or_element, PaneOptionComponentConfig);

        // Rather than individual data attributes being used for config we should move to using the JSON in the `datasetConfigKey` data attribute.
        this.setDatasetConfig(PaneOptionComponentConfig.datasetConfigKey);
        this.identifier = this.getIdentifier();
        this.statsIdentifier = this.getStatsIdentifier();
        this.paneIdentifier = this.getPaneIdentifier();
        this.hasNativeView = this.getNativeViewState(this.element);
        this.preventDefaultAction = this.getPreventDefaultActionDatasetConfig();
        this.alwaysOpenPane = this.getAlwaysOpenPaneDatasetConfig();
        this.navToggleInstruction = this.getNavToggleInstructionDatasetConfig();

        // the `willShowPaneOverride` flag can be set to a boolean to override the default behaviour for showing the associated pane.
        // For example, when closing a pane without a map filter (subpage) we "jump back" to the last option that did have a map filter.
        // In that case we don't want to show the associated pane so we use this flag to override the default behaviour to ensure the pane is not shown when it otherwise would be.
        this.willShowPaneOverride = null;

        // the `navToggleInstructionOverride` flag can be set to a string ('open' or 'close') to override the standard behaviour for the nav toggle instruction.
        // This allows the nav to be toggled open or closed when a pane option is selected. The actual toggling is handled by the online app or native app.
        // The instruction is recorded to a data attribute on the pane option component so that the app can read it and action the close/open.
        // If it's a value other than 'open' or 'close' then the default behaviour is used. If it's set to 'open' then the nav should be toggled open. If it's set to 'closed' then the nav should be toggled closed.
        this.navToggleInstructionOverride = null;

        this.primaryPane = primaryPane;
        this.panesManager = panesManager;
        this.associatedPane = this.getAssociatedPane();

        this.mapFilterCode = getMapFilterCode(this.element);
        this.hasMapFilter = getHasMapFilter(this.element);

        this.paneLinkComponent = PaneLinkComponent.new(this.element, this);
        this.paneInfoComponent = PaneInfoComponent.new(this.element, this);

        // Listen for nav option selection events that are triggered from the `OnlineAppNavOption` component.
        // This allows the Nav JS app to react to stuff that is handled by the OnlineApp.
        // These events are fired when the OnlineApp handles routes and selects an appropriate nav option based on that route.
        // For example a user may use the 'back' button and this changes the URL and kicks the OnlineAppRouter route handler into executing.
        // The handler dispatches an action that selects a navigation option and also triggers this event.
        // See `app/javascript/rankers/online_apps/online_app/instances/state_machine/actions.js#filterRouteRequested`
        this.element.addEventListener(OnlineAppNavOptionConfig.selectedWithRouteRequestEventName, (event) => {
            this.dispatchAction('selectPaneOptionComponentWithRouteRequest', this);
        });

        // Set up observer to handle capturing stats impressions (views) of the component within it's parent element.
        this.viewedObserver = null;
        this.initViewedObserver();

        // Add this pane option component to our global collection…
        if (!!this.primaryPane) primaryPaneOptionCollection.push(this);
    }

    getIdentifier() {
        const identifier = this.getDatasetConfigValue(PaneOptionComponentConfig.identifierDatasetConfigKey);
        if (!identifier) return;
        return identifier;
    }

    getStatsIdentifier() {
        const identifier = this.getDatasetConfigValue(PaneOptionComponentConfig.statsIdentifierDatasetConfigKey);
        if (!identifier) return;
        return identifier;
    }

    getPaneIdentifier() {
        const paneIdentifier = this.getDatasetConfigValue(PaneOptionComponentConfig.paneIdentifierDatasetConfigKey);
        if (!paneIdentifier) return;
        return paneIdentifier;
    }

    getNativeViewState() {
        const nativeViewState = this.getDatasetConfigValue(PaneOptionComponentConfig.nativeViewStateDatasetConfigKey);
        if (!nativeViewState) return;
        return nativeViewState;
    }

    getPreventDefaultActionDatasetConfig() {
        const preventDefaultAction = this.getDatasetConfigValue(PaneOptionComponentConfig.preventDefaultActionDatasetConfigKey);
        if (!preventDefaultAction) return;
        return preventDefaultAction;
    }

    // First check if the nav is in a native app (where we never prevent default actions on the navigation options).
    // Native apps never prevent default actions because the native code relies on these actions (and their URL) for routing.
    // Otherwise fall back to the configuration on the element.
    shouldPreventDefaultAction() {
        if (this.getNav().isNativeApp) return false;
        return !!this.preventDefaultAction;
    }

    getAlwaysOpenPaneDatasetConfig() {
        const alwaysOpenPane = this.getDatasetConfigValue(PaneOptionComponentConfig.alwaysOpenPaneDatasetConfigKey);
        if (typeof alwaysOpenPane === 'undefined' || alwaysOpenPane === null) return;
        return alwaysOpenPane;
    }

    getNavToggleInstructionDatasetConfig() {
        const navToggleInstruction = this.getDatasetConfigValue(PaneOptionComponentConfig.navToggleInstructionDatasetConfigKey);
        if (typeof navToggleInstruction === 'undefined' || navToggleInstruction === null) return;
        if (!isNavToggleInstructionValid(navToggleInstruction)) return;
        return navToggleInstruction;
    }

    /**
     * Returns the component's parent element that is used for detecting if the component is visible.
     *
     * @return {Node|undefined} The DOM element that the component's element scrolls within.
     */
    getParentScrollElement() {
        if (!this.primaryPane || !this.primaryPane.paneComponent) return;
        return this.primaryPane.paneComponent.element;
    }

    /**
     * Returns the Nav object associated with the panesManager
     *
     * @return {Nav|undefined} The Nav if present.
     */
    getNav() {
        if (!this.panesManager) return;
        return this.panesManager.nav;
    }

    /**
     * Returns the associated Pane either from the cached `associatedPane` instance variable or
     * by looking up the pane via the panesManager using the paneIdentifier.
     *
     * @return {Pane} The associated Pane.
     */
    getAssociatedPane() {
        if (this.associatedPane) return this.associatedPane;
        this.associatedPane = this.panesManager.findPaneByIdentifier(this.paneIdentifier);
        return this.associatedPane;
    }

    /**
     * Returns whether or not there is an associated Pane.
     *
     * @return {boolean} If there is an associated Pane.
     */
    hasAssociatedPane() {
        return !!this.getAssociatedPane();
    }

    /**
     * Returns whether the associated Pane has never been opened before.
     *
     * @return {boolean} The opened state of the associated Pane.
     */
    associatedPaneNeverOpened() {
        const pane = this.getAssociatedPane();
        if (!pane) return true;
        return !pane.hasOpened();
    }

    /**
     * Returns whether the associated Pane should always be opened.
     *
     * @return {boolean} Whether the associated pane should always be opened.
     */
    shouldAlwaysOpenPane() {
        return !!this.alwaysOpenPane;
    }

    /**
     * Returns the instruction string as to whether the nav should toggle open or closed.
     * Valid return values are 'open', 'close' or '' (empty string).
     * This is used to communicate with the native apps and the online app to decide if the nav should be open or closed when a pane option is selected.
     * The instruction can be manually overridden by setting the `navToggleInstructionOverride` property.
     * Otherwise the instruction is determined by any config or the `shouldOpenPane()` method (because the nav should be open when the pane is opened).
     *
     * @returns {string|null}
     */
    getNavToggleInstruction() {
        if (isNavToggleInstructionValid(this.navToggleInstructionOverride)) return this.navToggleInstructionOverride;
        if (isNavToggleInstructionValid(this.navToggleInstruction)) return this.navToggleInstruction;
        return this.shouldOpenPane() ? "open" : "close";
    }

    /**
     * Store whether we are showing a Nav Pane when a child component is clicked.
     * This method temporarily sets the element's `nav_is_showing_content` data attribute to true.
     * The native apps utilise this mechanism to decide if the "drawer" should be closed or not.
     * The timeout is set appropriately so that devices have time to do work and then catch this value.
     * On slow devices (Android) and filters (Everything) that have lots of markers to load this can be a while!
     */
    recordShowingContent() {
        this.element.dataset["nav_is_showing_content"] = "true";
        setTimeout(() => {
            this.element.dataset["nav_is_showing_content"] = "false";
        }, 1000);
    }

    isNavPaneShowingContent() {
        return this.element.dataset["nav_is_showing_content"] === "true";
    }

    /**
     * Set up an intersection observer for when the element is viewed within its scrollable parent element.
     * @return {undefined}
     */
    initViewedObserver() {
        if (!IntersectionObserver) return;
        let options = {
            root: this.getParentScrollElement(),
            rootMargin: "10px",
            threshold: 1.0,
        };
        this.viewedObserver = new IntersectionObserver(this.handleViewed.bind(this), options);
    }

    /**
     * Tell the viewed intersection observer to start observing.
     * @return {undefined}
     */
    observeViewed() {
        if (!this.viewedObserver) return;
        this.viewedObserver.observe(this.element);
    }

    /**
     * Tell the viewed intersection observer to stop observing.
     * @return {undefined}
     */
    unobserveViewed() {
        if (!this.viewedObserver) return;
        this.viewedObserver.unobserve(this.element);
    }

    /**
     * Callback for when the viewedObserver detects the component intersecting the parent scroll element.
     * @param entries {IntersectionObserverEntry}
     * @param observer {IntersectionObserver}
     */
    handleViewed(entries, observer) {
        entries.forEach((entry) => {
            if (entry && entry.isIntersecting) {
                this.dispatchAction("viewPaneOptionComponent", this);
            }
        });
    }

    /**
     * Determines if the associated Pane should be opened when selected or one of the PaneOptionComponent child components (link or info) is clicked.
     * The associated pane should be opened when the `willShowPaneOverride` property is specifically set. This can be manually set to force the pane to open or not open and override the standard behaviour.
     * The associated pane should be opened when the pane has never been opened before.
     * The associated pane should be opened when the pane option is marked to "always open associated pane".
     * @return {boolean} If the associated Pane should be opened.
     */
    shouldOpenPane() {
        if (typeof this.willShowPaneOverride === "boolean") return this.willShowPaneOverride;
        if (!this.hasAssociatedPane()) return false; // Should not open the Pane when there is no associated Pane.
        if (this.hasMapFilter === false) return true; // Should open the Pane when there is no map filter.
        if (this.shouldAlwaysOpenPane()) return true;
        return this.associatedPaneNeverOpened();
    }

    isSelected() {
        return (this.readStateMachineValue("currentPrimaryPaneOptionComponentId") === this.identifier);
    }

    render() {
        this.conditionallySetHtmlClassState(this.isSelected(), "selected");
        this.swapHtmlClassStates(this.hasAssociatedPane(), "has_pane", "has_no_pane");
        this.conditionallySetHtmlClassState(this.hasMapFilter, "has_map_filter");
        this.conditionallySetHtmlClassState(this.hasNativeView, "has_native_view");
    }

    trackViewed() {
        let nav = this.getNav();
        // We include the `eventLabel` option for backwards compatibility with older
        // native apps which only handled `eventAction` and `eventLabel` params (the `eventAction` comes from the `eventName`).
        if (nav && nav.rankersAnalyticsEventTracker && window.RankersAnalytics) {
            const backwardsCompatibilityOptions = {eventLabel: this.statsIdentifier}; // See tracker.js `getBackwardsCompatibilityParams()` function to understand this!
            const options = {nav_option_id: this.statsIdentifier};
            const allOptions = {...backwardsCompatibilityOptions, ...options};
            nav.rankersAnalyticsEventTracker(window.RankersAnalytics.EventName.NAV_OPTION_VIEWED, allOptions);
        }
    }

    trackOpened() {
        let nav = this.getNav();
        if (nav && nav.rankersAnalyticsEventTracker && window.RankersAnalytics) {
            const backwardsCompatibilityOptions = {eventLabel: this.statsIdentifier}; // See tracker.js `getBackwardsCompatibilityParams()` function to understand this!
            const options = {nav_option_id: this.statsIdentifier};
            const allOptions = {...backwardsCompatibilityOptions, ...options};
            nav.rankersAnalyticsEventTracker(window.RankersAnalytics.EventName.NAV_OPTION_SELECTED, allOptions);
        }
    }

    /**
     * Fire a click event on the PaneLinkComponent <a> element.
     * This is used to trigger the native apps and online apps to handle the navigation.
     * It's useful when we need the `Nav` and hosting apps (native or `OnlineApp`) to respond appropriately.
     * Note that we use the `click` function to fire the event (rather than a custom event) so we can't send a payload.
     * AFAIK the custom event was causing issues with the native apps. This should be investigated further at some point as a custom event with a payload would be cleaner.
     */
    fireClickEventOnLink(payload) {
        const target = this.element.querySelector(PaneLinkComponentConfig.selector);
        if (target) {
            // TODO - Investigate using a custom event with a payload.
            // payload = payload || {};
            // var customClickEvent = new CustomEvent('click', {
            //     detail: payload,    // Pass the payload as a detail object
            //     bubbles: true,       // Event will bubble up through the DOM
            //     cancelable: true     // Event can be canceled
            // });
            //
            // target.dispatchEvent(customClickEvent);

            target.click();
        }
    }
}

function getMapFilterCode(element) {
    const linkElement = element.querySelector(PaneLinkComponentConfig.selector);
    return linkElement.dataset[PaneLinkComponentConfig.mapFilterCodeDatasetKey];
}

function getHasMapFilter(element) {
    const mapFilterCode = getMapFilterCode(element);
    return typeof mapFilterCode != "undefined" && mapFilterCode !== "";
}

function isNavToggleInstructionValid(instruction) {
    return (
        instruction === "open" || instruction === "close" || instruction === ""
    );
}
